'use client';

import { useMemo } from 'react';
import {
  Badge,
  BodyDefault,
  Button,
  MetaDefault,
  MonoSmall,
} from '@terra/ui-design-system';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  parseCopilotActivityLog,
  type CopilotActivityLogEntry,
} from '@terra/shared-dto-chat-types';

const MAX_RENDERED_ENTRIES = 300;

const formatClock = (timestamp: string): string => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const formatDuration = (durationMs: number | undefined): string => {
  if (typeof durationMs !== 'number') {
    return '';
  }

  if (durationMs >= 1000) {
    return `${(durationMs / 1000).toFixed(1)}s`;
  }

  return `${durationMs}ms`;
};

const statusColor = (status: string): string => {
  if (status === 'error') {
    return 'fg.error';
  }

  if (status === 'success') {
    return 'fg.success';
  }

  return 'fg.muted';
};

const kindLabel = (entry: CopilotActivityLogEntry): string => {
  if (entry.kind === 'run') {
    return entry.status === 'start' ? 'run start' : 'run';
  }

  return entry.kind;
};

export function ActivityLogTimeline({
  content,
  onSelectPath,
}: {
  content: string;
  onSelectPath: (path: string) => void;
}) {
  const entries = useMemo(() => parseCopilotActivityLog(content), [content]);
  const renderedEntries = useMemo(
    () => entries.slice(-MAX_RENDERED_ENTRIES),
    [entries],
  );

  if (entries.length === 0) {
    return (
      <VStack flex="1" justifyContent="center" alignItems="center">
        <BodyDefault color="fg.muted">No activity recorded yet</BodyDefault>
      </VStack>
    );
  }

  return (
    <VStack alignItems="stretch" gap="1">
      {entries.length > renderedEntries.length ? (
        <MetaDefault color="fg.muted" px="2" py="1">
          Showing the latest {renderedEntries.length} of {entries.length}{' '}
          entries
        </MetaDefault>
      ) : null}
      {renderedEntries.map((entry, index) => (
        <HStack
          key={`${entry.timestamp}-${entry.name}-${index}`}
          alignItems="flex-start"
          gap="3"
          py="1.5"
          px="2"
          borderRadius="md"
          _hover={{ bg: 'bg.subtle' }}
        >
          <MonoSmall color="fg.subtle" flexShrink={0} minW="16">
            {formatClock(entry.timestamp)}
          </MonoSmall>
          <VStack alignItems="stretch" gap="0.5" flex="1" minW="0">
            <HStack gap="2" alignItems="center" flexWrap="wrap">
              <MetaDefault color="fg.muted">{entry.actor}</MetaDefault>
              <MetaDefault color="fg.default">
                {kindLabel(entry)} {entry.name}
              </MetaDefault>
              <Badge
                size="sm"
                variant="outline"
                color={statusColor(entry.status)}
              >
                {entry.status}
              </Badge>
              {formatDuration(entry.durationMs) ? (
                <MonoSmall color="fg.subtle">
                  {formatDuration(entry.durationMs)}
                </MonoSmall>
              ) : null}
            </HStack>
            {entry.summary ? (
              <BodyDefault color="fg.muted" truncate title={entry.summary}>
                {entry.summary}
              </BodyDefault>
            ) : null}
            {entry.artifactPath ? (
              <Button
                variant="ghost"
                size="xs"
                justifyContent="flex-start"
                onClick={() => onSelectPath(entry.artifactPath ?? '')}
              >
                <MonoSmall color="fg.info" truncate>
                  {entry.artifactPath}
                </MonoSmall>
              </Button>
            ) : null}
          </VStack>
        </HStack>
      ))}
    </VStack>
  );
}
